/**
 * Helpers for SharePoint Choice / MultiChoice options.
 *
 * The dropdown option key IS the choice string, and that key is what gets saved, so:
 *  - dedupeChoices collapses entries that differ only by surrounding whitespace
 *    (case-sensitive), keeping a defined choice string as the canonical key. SharePoint
 *    does not enforce uniqueness in a field's CHOICES when it is set through schema XML,
 *    CSOM or provisioning, and duplicate keys break Fluent's first-match selection.
 *  - resolveChoiceKey maps a stored or typed value onto the defined choice it matches
 *    (exact, then trimmed, then case-folded, which mirrors Fluent's own typed-text
 *    matching), so a padded or re-cased value still renders as selected without ever
 *    inventing a key that is not a defined choice.
 *
 * Stored values are never rewritten here: callers keep the raw value in state and only
 * hand the resolved key to the control.
 */

const isString = (value: unknown): value is string => typeof value === "string";

/** Returns the choices with whitespace-only duplicates removed. Non-array input is returned as is. */
export const dedupeChoices = <T>(choices: T): T => {
    if (!Array.isArray(choices)) {
        return choices;
    }

    const result: unknown[] = [];
    const indexByTrimmedKey = new Map<string, number>();
    const seenNonStrings = new Set<unknown>();

    for (const entry of choices) {
        if (!isString(entry)) {
            if (!seenNonStrings.has(entry)) {
                seenNonStrings.add(entry);
                result.push(entry);
            }
            continue;
        }

        const trimmed = entry.trim();
        const existingIndex = indexByTrimmedKey.get(trimmed);
        if (existingIndex === undefined) {
            indexByTrimmedKey.set(trimmed, result.length);
            result.push(entry);
        }
        else if (entry === trimmed && result[existingIndex] !== trimmed) {
            // A later entry that equals its own trim is a better canonical key than a padded one.
            result[existingIndex] = entry;
        }
    }

    return result as unknown as T;
};

/** Returns the field list with every choices array deduped; other fields and non-array input are returned as is. */
export const dedupeFieldChoices = <T extends { Choices?: any }>(fields: T[]): T[] => {
    if (!Array.isArray(fields)) {
        return fields;
    }
    return fields.map(field => (Array.isArray(field?.Choices) ? { ...field, Choices: dedupeChoices(field.Choices) } : field));
};

/**
 * Returns the defined choice a value matches, else the value unchanged. Exact match first, then
 * trimmed, then case-folded: Fluent matches typed text case-insensitively before it reaches a
 * change handler, so "OPEN" and "OPEN " must land on the same option. Always returns the
 * choice's own string on a match, never a re-cased or re-trimmed variant of it.
 */
export const resolveChoiceKey = (stored: any, choices: any): any => {
    if (!isString(stored) || !Array.isArray(choices)) {
        return stored;
    }
    if (choices.includes(stored)) {
        return stored;
    }

    const trimmed = stored.trim();
    if (!trimmed) {
        // A blank stored value must not latch onto a whitespace-only choice.
        return stored;
    }
    const trimmedMatch = choices.find(choice => isString(choice) && choice.trim() === trimmed);
    if (trimmedMatch !== undefined) {
        return trimmedMatch;
    }
    const folded = trimmed.toLocaleLowerCase();
    const foldedMatch = choices.find(choice => isString(choice) && choice.trim().toLocaleLowerCase() === folded);
    return foldedMatch !== undefined ? foldedMatch : stored;
};

const toArray = (value: any): any[] => (Array.isArray(value) ? value : value ? [value] : []);

/** Resolves every entry of a multi-value (array or single string) and drops exact duplicates. */
export const resolveChoiceKeys = (stored: any, choices: any): any[] => {
    const resolved: any[] = [];
    for (const entry of toArray(stored)) {
        const key = resolveChoiceKey(entry, choices);
        if (!resolved.includes(key)) {
            resolved.push(key);
        }
    }
    return resolved;
};

/**
 * Fill-in mode: appends a trimmed custom option for every stored entry that resolves to no
 * option key, resolving against the list as it grows so whitespace variants of one undefined
 * value share a single entry. Returns the same array when nothing needed adding.
 */
export const appendCustomOptions = <T extends { key: any; text: any }>(options: T[], stored: any): T[] => {
    let result = options;
    for (const entry of toArray(stored)) {
        if (!isString(entry)) {
            continue;
        }
        const keys = result.map(option => option.key);
        if (keys.includes(resolveChoiceKey(entry, keys))) {
            continue;
        }
        const custom = entry.trim();
        if (custom) {
            result = [...result, { key: custom, text: custom } as T];
        }
    }
    return result;
};

/** The deduped choices as a safe array: anything that is not an array (undefined, verbose OData objects) becomes []. */
export const toChoiceList = (choices: unknown): any[] => {
    const deduped = dedupeChoices(choices);
    return Array.isArray(deduped) ? deduped : [];
};
